import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ErrorNote } from "@/components/AppShell";
import HouseholdShell from "@/components/HouseholdShell";
import { globalApi } from "@/lib/api";
import { useSession } from "@/lib/session";
import type { Vessel } from "@/types";

interface ImportResult {
  vessel: Vessel;
  counts: Record<string, number>;
}

const COUNT_LABELS: Record<string, string> = {
  readings: "readings",
  doses: "doses",
  events: "events",
  equipment: "pieces of equipment",
  pump_schedules: "pump schedules",
  pump_windows: "pump run windows",
  photos: "photos",
  advice: "saved pieces of advice",
  chat_threads: "conversations",
  chat_messages: "messages",
};

/**
 * A new pool or hot tub from a Pool Nerd export file.
 *
 * Always new. Nothing you already have can be overwritten by a file, which
 * is what makes it safe to try on anything that looks like an export.
 */
export default function ImportVessel() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const body = new FormData();
      body.append("file", file);
      const imported = await globalApi.post<ImportResult>("/api/vessels/import", body);
      setResult(imported);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not import that file.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <HouseholdShell
      title="Import a pool or hot tub"
      subtitle="From a file exported by Pool Nerd."
      action={
        <Link to="/" className="text-sm text-(--color-ink-muted)">
          Cancel
        </Link>
      }
    >
      {result ? (
        <div className="card space-y-3 p-4">
          <p className="font-medium">
            {result.vessel.name} is in your household as a new {result.vessel.vessel_type === "hot_tub" ? "hot tub" : "pool"}.
          </p>
          <ul className="text-sm text-(--color-ink-muted)">
            {Object.entries(result.counts)
              .filter(([key, n]) => n > 0 && key in COUNT_LABELS)
              .map(([key, n]) => (
                <li key={key}>
                  {n} {COUNT_LABELS[key]}
                </li>
              ))}
            {result.counts.photos_without_image > 0 && (
              <li className="text-(--color-status-warn)">
                {result.counts.photos_without_image} photo{result.counts.photos_without_image === 1 ? "" : "s"} had no image in the file and
                {result.counts.photos_without_image === 1 ? " was" : " were"} left out.
              </li>
            )}
          </ul>
          <button type="button" className="btn btn-primary w-full" onClick={() => navigate(`/v/${result.vessel.id}`)}>
            Open it
          </button>
        </div>
      ) : (
        <form onSubmit={(e) => void submit(e)} className="space-y-4">
          <p className="text-sm text-(--color-ink-muted)">
            The file comes from <em>More → Export this pool as a file</em> in the top bar of any Pool Nerd account.
            Importing it makes a new pool here with every reading, dose, event, piece of equipment,
            pump schedule, photo and conversation it holds. Nothing you already have is changed.
          </p>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Export file</span>
            <input
              type="file"
              accept="application/json,.json"
              required
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm"
            />
          </label>
          {error && <ErrorNote message={error} />}
          <button type="submit" disabled={busy || !file} className="btn btn-primary w-full">
            {busy ? "Importing…" : "Import"}
          </button>
        </form>
      )}
    </HouseholdShell>
  );
}
